/**
 * Eva Super-Admin Usage view (SF-3736 / SF-3843).
 *
 * A separate "SF internal" view in the Eva section showing Eva usage by
 * super-admin (SF internal staff) users only, scoped to the `usage:superadmin:*`
 * rollups. Super-admin usage is tracked separately in Redis and is never counted
 * against any company — it is read here via the dedicated SF-3731 endpoints
 * (`/admin/eva/usage/superadmins` + series + `/live/superadmin`) and never mixed
 * into the platform (SF-3733) or company pages.
 *
 * SF-3843 re-aligns this page with the Company Detail conventions: a month/year
 * PeriodSelector (with the past-month override pattern so it auto-tracks the UTC
 * month rollover), live current-month tiles + "Live" badges, double trend charts
 * (rolling 12-month monthly + selected-month daily), a client-side top-spenders
 * box, and a client-side searchable "All Super-Admins" table.
 *
 * SF-4855 adds the overview page's two breakdowns for staff spend: "Spend by AI
 * Feature" (live split from `/live/superadmin` for the current month, the saved
 * `/admin/eva/usage/superadmins/entry-points` figures for past months or when
 * live is unavailable, chosen by the same pickEntryPointRows rule as the
 * overview) and "Top 10 Tools by Usage" (`/admin/eva/usage/superadmins/top-tools`,
 * so only tools Eva ran for staff are counted). Demo
 * building and fake demo data text count as staff spend, so they show here.
 *
 * Reuses the SF-3733 shared KpiTile / TrendChart / DataTable components and the
 * Eva API client. This is SF's own internal usage of Eva: it is gated on the
 * Eva read PERMISSION `admin:eva:usage:read` (SF-3736), not the super-admin
 * role, matching sf-api requirePermissions and the rest of the Eva section, and
 * no usage request is issued for users lacking it. Every super-admin record is
 * company-less by design, so nothing here references a company.
 *
 * PR-A scope: the super-admin population is small (<100), so the top-N ranking
 * and the search are computed CLIENT-SIDE over the full set fetched in one page
 * (limit 100). There is no server-side search param and the live endpoint is
 * aggregate-only (no per-user array), so live drives only the tiles.
 */
import { useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { Alert, Col, Input, Result, Row, Skeleton, Space, Spin, Tag, Typography } from 'antd';
import { DollarOutlined, MessageOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { KpiTile } from '../../components/kpi/KpiTile';
import { LiveIndicator } from '../../components/kpi/LiveIndicator';
import { TrendChart, type TrendPoint } from '../../components/charts/TrendChart';
import { DataTable } from '../../components/table/DataTable';
import { formatCurrency, formatNumber, EM_DASH } from '../../utils/format';
import { hasPermission } from '../../utils/permissions';
import {
  currentPeriod,
  monthRange,
  monthlyTrendRange,
  formatShortDate,
} from '../../utils/date';
import { PeriodSelector } from './components/PeriodSelector';
import { EntryPointBreakdown } from './components/EntryPointBreakdown';
import { pickEntryPointRows } from './components/entryPointRows';
import {
  useSuperadmins,
  useSuperadminsSeries,
  useLiveSuperAdminUsage,
  useSuperadminEntryPoints,
  useTopTools,
  EVA_READ_PERMISSION,
  type SuperadminUserRow,
  type SeriesPoint,
  type TopToolRow,
} from './api/evaUsageClient';
import { SuperadminUserDrawer } from './components/SuperadminUserDrawer';

const { Title, Text } = Typography;

/** Number of super-admins shown in the top-spenders table. */
const TOP_SPENDER_LIMIT = 5;

/**
 * Page size for the super-admin list. The super-admin population is small
 * (<100), so a single large page holds the full set — top-N and search are then
 * computed CLIENT-SIDE over it (PR-A scope; no server-side search param).
 */
const SUPERADMIN_LIST_LIMIT = 100;

/** Number of tools in the staff top-tools table (same as the overview). */
const TOP_TOOL_LIMIT = 10;

/** Map series points to the cost line plotted by a trend chart. */
function toCostTrend(series: SeriesPoint[] | undefined): TrendPoint[] {
  return (series ?? []).map((point) => ({
    bucket: point.bucket,
    value: Number.isFinite(Number(point.costUsd)) ? Number(point.costUsd) : 0,
  }));
}

/** True when a super-admin row has non-zero spend this period. */
function hasSpend(row: SuperadminUserRow): boolean {
  return Number(row.totals?.costUsd) > 0;
}

export const EvaSuperAdminsPage: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<SuperadminUserRow | null>(null);

  // SF-3736: this is SF's own internal usage of Eva. Gate on the Eva read
  // PERMISSION `admin:eva:usage:read` (via hasPermission, so the admin:eva:* / *
  // wildcards inherit it) rather than the super-admin ROLE — matching sf-api
  // requirePermissions on /admin/eva/usage/superadmins* and the rest of the Eva
  // section, so the nav entry, the page, and the server share one gate. Resolve
  // permissions before issuing any usage request so unauthorised users never
  // trigger a GET (mirrors the platform-defaults + overview access-control
  // pattern).
  const { data: permissions, isLoading: roleLoading } = usePermissions<string[]>();
  const canRead = hasPermission(permissions ?? [], EVA_READ_PERMISSION);
  const enabled = canRead;

  // SF-3843: the page can show any month from the floor → current month. The
  // selector drives every tile/table/chart. Mirrors company-detail exactly: do
  // NOT freeze the default with `useState(currentPeriod())` — a tab left open
  // across a UTC month boundary would keep the old month in state, silently
  // turn live off, and show the prior month's rollup until re-picked. Instead we
  // store only an EXPLICIT past-month override; while it is null the selected
  // period is derived from `currentPeriod()` every render so it tracks the real
  // current month across the rollover. Selecting the current month clears the
  // override (stays auto-tracking); selecting a past month pins it.
  const livePeriod = currentPeriod();
  const [periodOverride, setPeriodOverride] = useState<string | null>(null);
  const selectedPeriod =
    periodOverride !== null && periodOverride !== livePeriod ? periodOverride : livePeriod;
  const handlePeriodChange = (period: string) =>
    setPeriodOverride(period === currentPeriod() ? null : period);
  const thisPeriod = selectedPeriod;
  const isCurrentMonth = thisPeriod === currentPeriod();

  // Client-side search over the loaded super-admin set (PR-A scope). The full
  // set fits in one page (<100), so the input filters by name OR email
  // case-insensitively rather than threading a server-side `search` param.
  const [searchInput, setSearchInput] = useState('');

  // Derive the chart ranges on every render (like the period helpers above)
  // rather than memoizing with an empty dep list — otherwise a tab left open
  // across a UTC day/month boundary keeps requesting the original end date and
  // never picks up new buckets until a remount/reload. The daily range follows
  // the SELECTED month (clamped to today for the current month, full month for
  // past months); the monthly range stays a rolling 12-month window.
  const dailyRange = monthRange(selectedPeriod);
  const monthlyRange = monthlyTrendRange();

  // Rollup super-admin usage for the selected period: aggregate totals plus the
  // full per-super-admin list (limit 100). NOTE: the rollup lists ONLY
  // super-admins WITH activity this period — never-active super-admins are out
  // of scope for PR-A (deferred to a roster-endpoint follow-up).
  const usageQuery = useSuperadmins(
    thisPeriod,
    { limit: SUPERADMIN_LIST_LIMIT, offset: 0 },
    { enabled },
  );
  // LIVE current-month aggregate (chat-service-proxied). Aggregate-only: it
  // drives the TILES, never the per-user tables. Gated on the current month so
  // a selected past month stays pure historical rollup.
  const liveQuery = useLiveSuperAdminUsage({ enabled: enabled && isCurrentMonth });
  const dailySeriesQuery = useSuperadminsSeries(
    { ...dailyRange, granularity: 'daily' },
    { enabled },
  );
  const monthlySeriesQuery = useSuperadminsSeries(
    { ...monthlyRange, granularity: 'monthly' },
    { enabled },
  );
  // Staff spend by AI feature: the saved monthly split, used for past months
  // and as the current month's fallback when the live split is unavailable.
  const entryPointsQuery = useSuperadminEntryPoints(thisPeriod, { enabled });
  // Staff tool use for the selected month. `scope: 'superadmin'` reads the
  // staff-only counters, which never feed the platform or company rankings.
  const topToolsQuery = useTopTools(thisPeriod, 'monthly', TOP_TOOL_LIMIT, undefined, {
    enabled,
    scope: 'superadmin',
  });

  const usage = usageQuery.data?.data;
  const live = liveQuery.data?.data;

  // Live current-month aggregate. Treat it as available only on a clean
  // (non-error) response with a usable cost — React Query keeps the last
  // successful `data` even when the latest poll errors, so gating on `isError`
  // keeps the displayed figure and the "Live" badge from ever disagreeing. When
  // live is unavailable we transparently fall back to the Mongo rollup so the
  // page still shows a figure (matches company-detail), and we drop the badge.
  // INTENTIONAL PARITY (do not "tighten" to require every live field present):
  // this gate is identical to company-detail's `liveAvailable`, and the producer
  // (sf-chat-service getLiveSuperAdminUsage -> toCamelTotals) always emits the
  // full camelCase totals object plus a numeric `userCount` together, so a
  // present `costUsd` implies the rest are present. A single shared badge across
  // all three tiles is the established company-page convention.
  //
  // The `live.period === thisPeriod` check (as on the overview page) closes the
  // UTC month-rollover window: React Query still holds LAST month's payload
  // until the new month's poll lands, so without it last month's figure and the
  // "Live" badge would show on the new month. The same gate drives the tiles,
  // the badge and the feature split, so they always agree.
  const liveAvailable =
    isCurrentMonth &&
    !liveQuery.isError &&
    live?.totals?.costUsd != null &&
    live?.period === thisPeriod;

  // Spend by AI feature, chosen exactly as on the overview page. Live counts
  // only when it is available and carries the split (an older chat-service
  // sends none, so the saved figures are used).
  const liveSplit = liveAvailable ? live?.byEntryPoint : null;
  const entryPoints = pickEntryPointRows({
    isCurrentMonth,
    live: liveSplit,
    liveLoading: liveQuery.isLoading,
    liveError: liveQuery.isError || (!liveQuery.isLoading && !liveSplit),
    saved: entryPointsQuery.data?.data,
    savedLoading: entryPointsQuery.isLoading,
    savedError: entryPointsQuery.isError,
  });
  // Only an answer that says `superadmin` is shown as staff tools; anything
  // else counts as unavailable, never as staff use.
  const toolsAnswer = topToolsQuery.data?.data;
  const staffToolsAnswer = toolsAnswer?.scope === 'superadmin';
  const topTools = staffToolsAnswer ? (toolsAnswer?.tools ?? []) : [];
  const toolsUnavailable =
    topTools.length === 0 && (topToolsQuery.isError || (!!toolsAnswer && !staffToolsAnswer));

  // Headline numbers, all null-guarded. Current month prefers the live total;
  // past months use the rollup for the selected period.
  const cost = (liveAvailable ? live?.totals?.costUsd : usage?.totals?.costUsd) ?? null;
  const messageCount =
    (liveAvailable ? live?.totals?.messageCount : usage?.totals?.messageCount) ?? null;
  // Super-admin count: prefer the live `userCount` for the current month (fall
  // back gracefully when the field is absent), else the rollup pagination total.
  const liveUserCount = live?.userCount;
  const userCount = liveAvailable
    ? liveUserCount ?? usage?.pagination?.total ?? null
    : usage?.pagination?.total ?? null;

  // Treat LOADING distinct from EMPTY (Bugbot rule): keep the tile skeleton
  // until a value resolves OR every source that can supply it has settled. For
  // the current month both the live and rollup sources can supply the figure,
  // so gate on BOTH — never collapse the skeleton the moment one settles empty.
  const tilesLoading = isCurrentMonth
    ? cost === null && (liveQuery.isLoading || usageQuery.isLoading)
    : usageQuery.isLoading;

  const dailyTrend = toCostTrend(dailySeriesQuery.data?.data?.series);
  const monthlyTrend = toCostTrend(monthlySeriesQuery.data?.data?.series);

  // The full super-admin set for the selected period (rollup only — the live
  // endpoint is aggregate-only with no per-user array). Both tables derive from
  // this client-side.
  const allUsers = usage?.users ?? [];

  // Top spenders: cost-sorted, zero-spend rows EXCLUDED (the Top table ranks
  // SPENDERS, matching the company Top Users), then sliced to the top N — all
  // client-side over the loaded set.
  const topSpenders = [...allUsers]
    .filter(hasSpend)
    .sort((a, b) => Number(b.totals?.costUsd ?? 0) - Number(a.totals?.costUsd ?? 0))
    .slice(0, TOP_SPENDER_LIMIT);

  // Client-side case-insensitive filter by name OR email over the loaded set.
  const searchTerm = searchInput.trim().toLowerCase();
  const filteredUsers = searchTerm
    ? allUsers.filter(
        (row) =>
          (row.userName ?? '').toLowerCase().includes(searchTerm) ||
          (row.userEmail ?? '').toLowerCase().includes(searchTerm),
      )
    : allUsers;

  // The live query is supplementary and degrades to the rollup on its own, so
  // its failure is deliberately EXCLUDED from the page-level partial-load
  // warning (matches company-detail's `anyError`) — a missing live endpoint
  // must not trigger the warning while the rollup is serving the figures.
  // The tools table joins the warning as on the overview; the feature split
  // shows its own "unavailable" state instead.
  const anyError =
    usageQuery.isError ||
    dailySeriesQuery.isError ||
    monthlySeriesQuery.isError ||
    topToolsQuery.isError;

  const topSpenderColumns: ColumnsType<SuperadminUserRow> = [
    {
      title: 'Super-Admin',
      key: 'user',
      render: (_v, row) => row.userName || row.userEmail || row.userId || EM_DASH,
    },
    {
      title: 'Spend',
      key: 'cost',
      align: 'right',
      render: (_v, row) => formatCurrency(row.totals?.costUsd),
    },
    {
      title: 'Messages',
      key: 'messages',
      align: 'right',
      render: (_v, row) => formatNumber(row.totals?.messageCount ?? null),
    },
    {
      title: 'Last Used',
      key: 'lastActive',
      align: 'right',
      render: (_v, row) => formatShortDate(row.lastActiveAt),
    },
  ];

  const userColumns: ColumnsType<SuperadminUserRow> = [
    {
      title: 'Super-Admin',
      key: 'user',
      render: (_v, row) => row.userName || row.userEmail || row.userId || EM_DASH,
    },
    {
      title: 'Email',
      dataIndex: 'userEmail',
      key: 'userEmail',
      render: (email: string | null) => email || EM_DASH,
    },
    {
      title: 'Spend',
      key: 'cost',
      align: 'right',
      render: (_v, row) => formatCurrency(row.totals?.costUsd),
    },
    {
      title: 'Messages',
      key: 'messages',
      align: 'right',
      render: (_v, row) => formatNumber(row.totals?.messageCount ?? null),
    },
    {
      title: 'Last Used',
      key: 'lastActiveAt',
      align: 'right',
      render: (_v, row) => formatShortDate(row.lastActiveAt),
    },
  ];

  // The tool rollups carry only a name and a count, no cost (as on the overview).
  const topToolColumns: ColumnsType<TopToolRow> = [
    {
      title: 'Tool',
      dataIndex: 'tool',
      key: 'tool',
      render: (tool: string | null) => tool || EM_DASH,
    },
    {
      title: 'Usage Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      render: (count?: number) => formatNumber(count ?? null),
    },
  ];

  if (roleLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }

  if (!enabled) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="The SF internal Eva usage view requires the admin:eva:usage:read permission."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Row justify="space-between" align="middle" wrap gutter={[16, 8]}>
          <Col flex="auto">
            <Space align="center">
              <Title level={3} style={{ marginBottom: 0 }}>
                Eva — SF Internal Usage
              </Title>
              <Tag color="purple">Super-admins</Tag>
            </Space>
            <div>
              <Text type="secondary">
                {isCurrentMonth ? 'Current month' : 'Historical'} ({selectedPeriod}) Eva usage by SF
                internal staff. Tracked separately and never counted against any company.
              </Text>
            </div>
          </Col>
          <Col>
            <PeriodSelector
              value={selectedPeriod}
              onChange={handlePeriodChange}
              disabled={!enabled}
            />
          </Col>
        </Row>
      </div>

      {anyError && (
        <Alert
          type="warning"
          showIcon
          message="Some usage data could not be loaded"
          description="One or more Eva usage endpoints did not respond. Figures may be incomplete."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Total Spend"
            value={formatCurrency(cost)}
            icon={<DollarOutlined />}
            loading={tilesLoading}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={
              liveAvailable
                ? 'Live current-month super-admin spend, updated without waiting for the nightly rollup'
                : 'Total super-admin Eva spend in the selected period'
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Messages"
            value={formatNumber(messageCount)}
            icon={<MessageOutlined />}
            loading={tilesLoading}
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint="Total super-admin Eva messages in the selected period"
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <KpiTile
            title="Super-Admins"
            value={formatNumber(userCount)}
            icon={<TeamOutlined />}
            loading={tilesLoading}
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint="Distinct super-admins with Eva activity in the selected period"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <TrendChart
            title="Monthly Spend (12 months)"
            data={monthlyTrend}
            granularity="monthly"
            loading={monthlySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            title={`Daily Spend (${isCurrentMonth ? 'this month to date' : selectedPeriod})`}
            data={dailyTrend}
            granularity="daily"
            loading={dailySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
      </Row>

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Top {TOP_SPENDER_LIMIT} Super-Admins by Spend
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
        </Space>
        <DataTable<SuperadminUserRow>
          data={topSpenders}
          columns={topSpenderColumns}
          loading={usageQuery.isLoading}
          rowKey={(row, index) => row.userId ?? row.userEmail ?? `top-${index}`}
          pagination={false}
          emptyText="No super-admin spend this period"
          onRowClick={(row) => setSelectedUser(row)}
        />
      </div>

      <EntryPointBreakdown
        title="Spend by AI Feature"
        period={thisPeriod}
        rows={entryPoints.rows}
        isLive={entryPoints.isLive}
        loading={entryPoints.loading}
        failed={entryPoints.failed}
      />

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Top {TOP_TOOL_LIMIT} Tools by Usage
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
        </Space>
        {/* The table's own spinner still shows its empty text, so keep a
            placeholder until the first answer arrives rather than saying
            there was no staff tool usage. */}
        {topToolsQuery.isLoading ? (
          <div role="status" aria-label="Loading staff tool usage">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : (
          <DataTable<TopToolRow>
            data={topTools}
            columns={topToolColumns}
            rowKey={(row, index) => row.tool ?? `tool-${index}`}
            pagination={false}
            emptyText={
              toolsUnavailable
                ? 'Staff tool usage is unavailable right now'
                : 'No staff tool usage this period'
            }
          />
        )}
      </div>

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            All Super-Admins
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
        </Space>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ maxWidth: 320, marginBottom: 12 }}
          aria-label="Search super-admins by name or email"
        />
        <DataTable<SuperadminUserRow>
          data={filteredUsers}
          columns={userColumns}
          loading={usageQuery.isLoading}
          rowKey={(row, index) => row.userId ?? row.userEmail ?? `row-${index}`}
          emptyText={
            searchTerm
              ? `No super-admins match “${searchInput.trim()}”`
              : 'No super-admin usage this period'
          }
          onRowClick={(row) => setSelectedUser(row)}
        />
      </div>

      <SuperadminUserDrawer
        user={selectedUser}
        period={thisPeriod}
        enabled={enabled}
        onClose={() => setSelectedUser(null)}
      />
    </Space>
  );
};
